import { api } from '../api/client';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ADDONS,
  type CostNature,
  type CatalogPricesProposal,
  type DuplicadoInfo,
  type ExpenseDraft,
  type ExpenseEntry,
  type ExpenseInput,
  type InvoiceStatus,
  type PaymentType,
  type PurchaseDocumentType,
} from '@alexiarest/shared';
import { isDemoMode } from '../api/client';
import { usePeriod } from '../context/PeriodContext';
import { useSaveExpense, useSaveSupplier, useScanQuota, useScanReceipt, useSuppliers } from '../api/hooks';
import { formatDate, formatMoney, parseDecimal } from '../format';
import { CATEGORY_OPTIONS, DELIVERY_NOTE_LABEL, DELIVERY_NOTE_LABEL_LOWER } from '../labels';
import { Drawer } from './Drawer';
import { ACCEPT_ESCANER, FORMATOS_ESCANER, prepararImagen } from '../imagenEscaner';
import { formatDecimalInput } from '../format';
import { CheckboxField, FieldRow, SelectField, TextField } from './Field';
import { useToast } from './Toast';
import { InvoiceCatalogProposal } from './InvoiceCatalogProposal';

const OPCIONES_IMAGEN = {
  maxLado: 2000,
  maxPdfBytes: 8_000_000,
  errorPdf: 'Ese PDF pesa demasiado. Exporta solo la página de la factura o haz una foto.',
  errorTamanyo: 'La imagen es demasiado grande. Prueba a hacer la foto más de cerca.',
};

/** Marca para "crear un proveedor nuevo" en el select de proveedor. */
const NUEVO_PROVEEDOR = '__nuevo__';

/**
 * Escaner de facturas: de una foto a un gasto.
 *
 * La regla que ordena toda la pantalla: **esto propone, no guarda**. Lo que
 * devuelve el OCR aparece en un formulario editable, con los campos dudosos
 * marcados, y el gasto no entra en la cuenta de resultados hasta que alguien
 * pulsa Guardar. Un importe mal leido que se cuela solo descuadra el mes y
 * cuando se detecta ya nadie sabe de donde salio.
 */



export function ScanReceiptDrawer({
  open,
  onClose,
  documentType = 'factura',
  onGuardado,
  initialDraft,
  confirmExpense,
}: {
  open: boolean;
  onClose: () => void;
  documentType?: PurchaseDocumentType;
  /**
   * La factura recien guardada, para que la pantalla pueda proponer conciliarla
   * con los albaranes que estaban esperandola (9 sep 2026, Alex). El escaner no
   * decide nada: solo avisa de que hay una factura nueva.
   */
  onGuardado?: (expense: ExpenseEntry) => void;
  initialDraft?: { proposal: ExpenseDraft; key?: string };
  confirmExpense?: (input: ExpenseInput, acceptPossibleDuplicate?: boolean) => Promise<ExpenseEntry>;
}): JSX.Element | null {
  if (!open) return null;
  return <Cuerpo onClose={onClose} documentType={documentType} onGuardado={onGuardado} initialDraft={initialDraft} confirmExpense={confirmExpense} />;
}

function Cuerpo({
  onClose,
  documentType,
  onGuardado,
  initialDraft,
  confirmExpense,
}: {
  onClose: () => void;
  documentType: PurchaseDocumentType;
  onGuardado?: (expense: ExpenseEntry) => void;
  initialDraft?: { proposal: ExpenseDraft; key?: string };
  confirmExpense?: (input: ExpenseInput, acceptPossibleDuplicate?: boolean) => Promise<ExpenseEntry>;
}): JSX.Element {
  const toast = useToast();
  const quotaQuery = useScanQuota();
  const escanear = useScanReceipt();
  const qc = useQueryClient();
  const [draftId, setDraftId] = useState<string>();
  const guardar = useSaveExpense(async (input) => {
    if (confirmExpense) return confirmExpense(input, duplicadoAsumido);
    if (!draftId) return api.createExpense(input);
    const saved = await api.confirmDraft(draftId, { expense: input, acceptPossibleDuplicate: duplicadoAsumido });
    if (!saved.result?.expenseId) throw new Error('El gasto no se ha confirmado.');
    void qc.invalidateQueries({ queryKey: ['drafts'] });
    return api.getExpense(saved.result.expenseId);
  });
  const suppliersQuery = useSuppliers();
  const saveSupplier = useSaveSupplier();
  const { period } = usePeriod();

  const [nombreFichero, setNombreFichero] = useState('');
  const [error, setError] = useState('');
  const [draft, setDraft] = useState<ExpenseDraft | null>(initialDraft?.proposal ?? null);
  const [catalogPrices, setCatalogPrices] = useState<CatalogPricesProposal>();
  const [catalogDraftId, setCatalogDraftId] = useState<string>();
  // Alta de proveedor al vuelo desde el escáner (tiene la factura delante).
  const [creandoProveedor, setCreandoProveedor] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState('');
  // Texto tal cual lo teclea el usuario: si el value fuera String(numero),
  // cada tecla se reformatearia y la coma decimal no entraria nunca.
  const [baseTxt, setBaseTxt] = useState(initialDraft ? formatDecimalInput(initialDraft.proposal.netAmount, 2) : '');
  const [vatTxt, setVatTxt] = useState(initialDraft ? formatDecimalInput(initialDraft.proposal.vatPct, 2) : '');
  const [key, setKey] = useState<string | undefined>(initialDraft?.key);
  // Duplicado: hay que decidir a mano. Empieza siempre en false, tambien al
  // escanear otra factura, para que un "guardar igualmente" no se herede.
  const [duplicadoAsumido, setDuplicadoAsumido] = useState(false);

  const quota = quotaQuery.data;
  const bloqueado = initialDraft ? false : quota ? quota.sinModulo || quota.exhausted : false;

  const resetProveedor = () => {
    setCreandoProveedor(false);
    setNuevoProveedor('');
  };

  const cargar = async (file: File) => {
    setError('');
    setDraft(null);
    setDuplicadoAsumido(false);
    resetProveedor();
    setNombreFichero(file.name);
    try {
      const blob = await prepararImagen(file, OPCIONES_IMAGEN);
      const res = await escanear.mutateAsync({ blob, documentType });
      setDraft(documentType === 'albaran' ? { ...res.draft, paid: false } : res.draft);
      setBaseTxt(res.draft.netAmount ? formatDecimalInput(res.draft.netAmount, 2) : '');
      setVatTxt(formatDecimalInput(res.draft.vatPct, 2));
      setKey(res.key);
      setDraftId(res.draftId);
      setCatalogPrices(res.catalogPrices);
      setCatalogDraftId(res.catalogDraftId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No hemos podido leer el documento.');
    }
  };

  const probarEjemplo = async () => {
    setError('');
    setDuplicadoAsumido(false);
    resetProveedor();
    setNombreFichero('factura-de-ejemplo.jpg');
    try {
      const res = await escanear.mutateAsync({ blob: new Blob([], { type: 'image/jpeg' }), documentType });
      setDraft(documentType === 'albaran' ? { ...res.draft, paid: false } : res.draft);
      setBaseTxt(res.draft.netAmount ? formatDecimalInput(res.draft.netAmount, 2) : '');
      setVatTxt(formatDecimalInput(res.draft.vatPct, 2));
      setKey(res.key);
      setDraftId(res.draftId);
      setCatalogPrices(res.catalogPrices);
      setCatalogDraftId(res.catalogDraftId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No hemos podido leer el documento.');
    }
  };

  const campo = <K extends keyof ExpenseDraft>(k: K, v: ExpenseDraft[K]) =>
    setDraft((prev) => (prev ? { ...prev, [k]: v } : prev));

  const crearProveedor = (nombreInicial?: string) => {
    const nombre = (nombreInicial ?? nuevoProveedor).trim();
    if (!nombre) return;
    saveSupplier.mutate(
      { input: { name: nombre } },
      {
        onSuccess: (creado) => {
          // Queda enganchado al gasto y desaparece el aviso de "no está en tus
          // proveedores".
          setDraft((prev) =>
            prev ? { ...prev, supplierId: creado.supplierId, supplierName: creado.name } : prev,
          );
          resetProveedor();
          toast.success(`Proveedor "${creado.name}" creado.`);
        },
        onError: (error) => toast.fail(error),
      },
    );
  };

  /** Cambiar el tipo de pago mantiene coherentes vencimiento y estado. */
  const cambiarTipoPago = (tipo: PaymentType) => {
    setDraft((prev) => {
      if (!prev) return prev;
      if (tipo === 'contado') {
        // Contado = pagado y sin vencimiento (como en el alta manual).
        return { ...prev, paymentType: 'contado', dueDate: undefined, paid: true };
      }
      return { ...prev, paymentType: 'credito', paid: false };
    });
  };

  const cambiarBase = (texto: string) => {
    setBaseTxt(texto);
    campo('netAmount', parseDecimal(texto));
  };

  /**
   * Corregir el IVA recalcula la base a partir del TOTAL leido en el
   * documento. Es el arreglo del ticket de Pol: si el desglose salio con un
   * tipo equivocado, cambiar el tipo lo endereza todo — sin hacer la division
   * a mano. Si no hay total leido, el IVA cambia y la base se queda.
   */
  const cambiarIva = (texto: string) => {
    setVatTxt(texto);
    /*
     * Vaciar la casilla para teclear otro numero NO es "IVA 0 %".
     *
     * Lo era: `parseDecimal('')` devuelve 0, y con total/(1+0) la base pasaba
     * a ser el TOTAL CON IVA. Alex escaneo una factura que se habia leido bien
     * (base 171,30) y acabo guardada como 188,43 al 0 %. Mientras la casilla
     * este vacia no se toca nada; el borrador conserva el ultimo tipo valido.
     */
    if (texto.trim() === '') return;
    const vat = parseDecimal(texto);
    setDraft((prev) => {
      if (!prev) return prev;
      if (prev.totalLeido != null && vat >= 0 && vat < 100) {
        const base = Math.round((prev.totalLeido / (1 + vat / 100)) * 100) / 100;
        setBaseTxt(formatDecimalInput(base, 2));
        return { ...prev, vatPct: vat, netAmount: base };
      }
      return { ...prev, vatPct: vat };
    });
  };

  // Con un duplicado sin resolver, Guardar esta apagado. No es un aviso mas:
  // guardar dos veces la misma factura descuadra el mes y las cuentas por
  // pagar, y se detecta semanas despues.
  const bloqueadoPorDuplicado = Boolean(draft?.duplicado) && !duplicadoAsumido;

  const confirmar = () => {
    if (!draft) return;
    if (bloqueadoPorDuplicado) return;
    if (draft.netAmount <= 0) {
      toast.notify('Escribe el importe antes de guardar.', 'error');
      return;
    }
    const input: ExpenseInput = {
      documentType,
      date: draft.date,
      dueDate: draft.dueDate,
      concept: draft.concept,
      supplierId: draft.supplierId,
      invoiceNumber: draft.invoiceNumber,
      category: draft.category,
      nature: draft.nature,
      netAmount: draft.netAmount,
      vatPct: draft.vatPct,
      paymentType: draft.paymentType,
      invoiceStatus: documentType === 'albaran' ? 'pendiente' : draft.invoiceStatus,
      paid: draft.paid,
      // El documento queda enganchado al gasto: es el justificante.
      attachmentKey: key && !key.startsWith('demo/') ? key : undefined,
    };
    guardar.mutate(
      { input },
      {
        onSuccess: (guardado) => {
          // Una factura de agosto guardada mientras miras julio se guarda
          // bien pero no aparece en la tabla, y parece que se ha perdido.
          const fuera = draft.date < period.start || draft.date > period.end;
          toast.success(
            fuera
              ? `Gasto guardado con fecha ${formatDate(draft.date)}. No lo ves en la tabla porque está fuera del periodo que tienes seleccionado.`
              : documentType === 'albaran' ? `${DELIVERY_NOTE_LABEL} guardada como compra provisional.` : 'Gasto guardado desde la factura.',
          );
          onClose();
          if (documentType !== 'albaran') onGuardado?.(guardado);
        },
        onError: (e) => toast.fail(e),
      },
    );
  };

  const total = draft ? draft.netAmount * (1 + draft.vatPct / 100) : 0;

  return (
    <Drawer
      open
      title={documentType === 'albaran' ? `Escanear una ${DELIVERY_NOTE_LABEL_LOWER}` : 'Escanear una factura'}
      subtitle={documentType === 'albaran'
        ? 'Quedará pendiente de factura y no entrará como gasto definitivo hasta conciliarla.'
        : 'Sube una foto o PDF y Alexia extraerá los datos para proponerte el gasto. Nada se guardará hasta que lo confirmes.'}
      onClose={onClose}
      width="42rem"
      footer={
        draft ? (
          <>
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn"
              disabled={guardar.isPending || bloqueadoPorDuplicado}
              onClick={confirmar}
            >
              {guardar.isPending
                ? 'Guardando…'
                : draft.duplicado && duplicadoAsumido
                  ? 'Guardar de todos modos'
                  : 'Guardar gasto'}
            </button>
          </>
        ) : undefined
      }
    >
      <div className="stack">
        {quota && bloqueado && (
          <p className="alerta">
            {quota.message ??
              `El escáner es un módulo aparte: ${ADDONS.escaner.pricePerMonth} € al mes.`}
          </p>
        )}

        {quota && !bloqueado && (
          <p className="field__hint">
            Escaneos disponibles este mes: <strong>{quota.remaining}</strong>
            {quota.limit ? ` de ${quota.limit}` : ''}
          </p>
        )}

        {!bloqueado && (
          <div className="row">
            <label className="btn btn--ghost">
              {escanear.isPending ? 'Leyendo…' : 'Elegir foto o PDF'}
              <input
                type="file"
                accept={ACCEPT_ESCANER}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void cargar(file);
                  e.target.value = '';
                }}
              />
            </label>
            {isDemoMode && (
              <button type="button" className="btn btn--ghost" onClick={() => void probarEjemplo()}>
                Probar con una factura de ejemplo
              </button>
            )}
            {nombreFichero && <span className="field__hint">{nombreFichero}</span>}
          </div>
        )}
        {!bloqueado && <p className="field__hint">{FORMATOS_ESCANER}</p>}

        {error && <p className="alerta alerta--error">{error}</p>}

        {draft && (
          <>
            {draft.duplicado && (
              <DuplicadoAviso
                info={draft.duplicado}
                asumido={duplicadoAsumido}
                onAsumir={setDuplicadoAsumido}
                onDescartar={onClose}
              />
            )}

            {draft.avisos.length > 0 && (
              <div className="form__section">
                <p className="form__section-title">Compruébalo antes de guardar</p>
                <ul className="lista-problemas">
                  {draft.avisos.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
            )}

            <form className="form" onSubmit={(e) => e.preventDefault()}>
              <TextField
                label="Concepto"
                value={draft.concept}
                onChange={(v) => campo('concept', v)}
              />

              {/* Proveedor: elegir uno existente o crearlo al vuelo. */}
              <SelectField
                label="Proveedor"
                value={draft.supplierId ?? ''}
                onChange={(value) => {
                  if (value === NUEVO_PROVEEDOR) {
                    setCreandoProveedor(true);
                    setNuevoProveedor(draft.supplierName ?? '');
                    return;
                  }
                  setCreandoProveedor(false);
                  campo('supplierId', value || undefined);
                }}
                options={[
                  { value: '', label: 'Sin proveedor asignado' },
                  ...(suppliersQuery.data ?? []).map((s) => ({ value: s.supplierId, label: s.name })),
                  { value: NUEVO_PROVEEDOR, label: '➕ Crear un proveedor nuevo…' },
                ]}
                hint={
                  !draft.supplierId && draft.supplierName && !creandoProveedor
                    ? `Detectamos “${draft.supplierName}” en la factura, pero no está registrado como proveedor.`
                    : undefined
                }
              />
              {!draft.supplierId && draft.supplierName && !creandoProveedor && (
                <div className="row">
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={saveSupplier.isPending}
                    onClick={() => crearProveedor(draft.supplierName)}
                  >
                    {saveSupplier.isPending ? 'Creando…' : `Crear «${draft.supplierName}» como proveedor`}
                  </button>
                </div>
              )}
              {creandoProveedor && (
                <div className="form__inline">
                  <TextField
                    label="Nombre del proveedor nuevo"
                    value={nuevoProveedor}
                    onChange={setNuevoProveedor}
                    placeholder="Distribuciones del Norte"
                    hint="Se crea al momento y queda enganchado a este gasto."
                  />
                  <div className="row" style={{ alignItems: 'flex-end', gap: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => crearProveedor()}
                      disabled={saveSupplier.isPending || nuevoProveedor.trim() === ''}
                    >
                      {saveSupplier.isPending ? 'Creando…' : 'Crear'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={resetProveedor}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
              <FieldRow>
                <TextField
                  label="Fecha"
                  type="date"
                  value={draft.date}
                  onChange={(v) => campo('date', v)}
                  error={draft.confianza.fecha === 'baja' ? 'Compruébala' : undefined}
                />
                <TextField
                  label={documentType === 'albaran' ? 'Nº de documento' : 'Nº de factura'}
                  value={draft.invoiceNumber ?? ''}
                  onChange={(v) => campo('invoiceNumber', v || undefined)}
                />
              </FieldRow>
              <FieldRow>
                <TextField
                  label="Importe sin IVA"
                  value={baseTxt}
                  onChange={cambiarBase}
                  inputMode="decimal"
                  error={draft.confianza.importe === 'baja' ? 'No lo hemos leído bien' : undefined}
                />
                <TextField
                  label="IVA de la compra (%)"
                  value={vatTxt}
                  onChange={cambiarIva}
                  inputMode="decimal"
                  hint={draft.totalLeido != null ? 'Al cambiar el IVA, el importe sin IVA se recalcula a partir del total.' : undefined}
                />
              </FieldRow>
              <FieldRow>
                <SelectField
                  label="Categoría"
                  value={draft.category}
                  onChange={(v) => campo('category', v as ExpenseDraft['category'])}
                  options={CATEGORY_OPTIONS}
                  hint="Alexia ha sugerido esta categoría según el proveedor. Cámbiala si no corresponde."
                />
                <SelectField
                  label="Tipo de coste"
                  value={draft.nature}
                  onChange={(v) => campo('nature', v as CostNature)}
                  options={[
                    { value: 'fijo', label: 'Fijo' },
                    { value: 'variable', label: 'Variable' },
                  ]}
                  hint="¿Es un coste fijo o cambia según la actividad del negocio?"
                />
              </FieldRow>

              {/* Cómo se paga y en qué estado (antes se deducía y no se veía). */}
              <FieldRow>
                <SelectField
                  label="Condición de pago"
                  value={draft.paymentType}
                  onChange={(v) => cambiarTipoPago(v as PaymentType)}
                  options={[
                    { value: 'contado', label: 'Contado' },
                    { value: 'credito', label: 'Crédito' },
                  ]}
                />
                <TextField
                  label="Vencimiento"
                  type="date"
                  value={draft.dueDate ?? ''}
                  onChange={(v) => campo('dueDate', v || undefined)}
                  disabled={draft.paymentType !== 'credito'}
                  hint={draft.paymentType !== 'credito' ? 'Solo a crédito.' : undefined}
                />
              </FieldRow>
              <FieldRow>
                <SelectField
                  label="Estado de la factura"
                  value={draft.invoiceStatus}
                  onChange={(v) => campo('invoiceStatus', v as InvoiceStatus)}
                  options={[
                    { value: 'facturado', label: 'Factura recibida' },
                    { value: 'pendiente', label: 'Pendiente de factura' },
                    { value: 'sin_factura', label: 'Sin factura' },
                  ]}
                />
                <CheckboxField
                  label={documentType === 'albaran' ? 'Ya la pagué al recibirla' : 'Ya está pagado'}
                  checked={draft.paid}
                  onChange={(v) => campo('paid', v)}
                />
              </FieldRow>
              <p className="form__section-hint">
                Total con IVA: <strong>{formatMoney(total)}</strong>
                {draft.totalLeido != null && Math.abs(total - draft.totalLeido) > 0.02 && (
                  <> · en la factura pone {formatMoney(draft.totalLeido)}</>
                )}
                {draft.paymentType === 'credito' && draft.dueDate && (
                  <> · a crédito, vence el {draft.dueDate}</>
                )}
              </p>
            </form>
            {catalogPrices && <InvoiceCatalogProposal key={nombreFichero} proposal={catalogPrices} />}
          </>
        )}
      </div>
    </Drawer>
  );
}

/**
 * Aviso de factura repetida.
 *
 * Deliberadamente NO es una linea mas en la lista de avisos: ensena el gasto
 * con el que choca —fecha, concepto, numero e importe— para que se pueda
 * comparar sin salir de aqui, y **apaga el boton de guardar** hasta que
 * alguien decide. Guardar dos veces la misma factura descuadra el mes y las
 * cuentas por pagar, y no se detecta hasta semanas despues, cuando ya nadie
 * recuerda que paso.
 *
 * Las dos salidas son igual de faciles: descartar (que es lo que toca casi
 * siempre) o guardarlo igualmente marcando la casilla.
 */
function DuplicadoAviso({
  info,
  asumido,
  onAsumir,
  onDescartar,
}: {
  info: DuplicadoInfo;
  asumido: boolean;
  onAsumir: (v: boolean) => void;
  onDescartar: () => void;
}): JSX.Element {
  const total = info.existente.netAmount * (1 + info.existente.vatPct / 100);
  const seguro = info.certeza === 'segura';

  return (
    <div className={`alerta ${seguro ? 'alerta--error' : ''}`.trim()}>
      <p>
        <strong>Parece que esta factura ya está registrada.</strong>{' '}
        {info.explicacion}
      </p>

      <div className="duplicado__ficha">
        <span className="duplicado__etiqueta">FACTURA YA REGISTRADA</span>
        <div>
          <strong>{info.existente.concept}</strong>
          {info.existente.invoiceNumber && <> · {info.existente.invoiceNumber}</>}
        </div>
        <div className="cell-sub">
          {formatDate(info.existente.date)} · {formatMoney(total)} con IVA
        </div>
      </div>

      <div className="row">
        <button type="button" className="btn btn--sm" onClick={onDescartar}>
          Descartar este escaneo
        </button>
        <label className="duplicado__confirmar">
          <input type="checkbox" checked={asumido} onChange={(e) => onAsumir(e.target.checked)} />
          No es la misma factura
        </label>
      </div>
    </div>
  );
}
